import { isValidGtin, normalizeCode } from "../../components/scanner/classify"

/**
 * A ler séries de um artigo (com séries, ou sem rastreio com «Ler séries»),
 * um código que a app não conta logo como série dá escolhas explícitas a quem
 * lê — em vez de a app adivinhar e deixar becos sem saída (0.42.7):
 *
 *  - **parece um EAN** (`parece_ean`): «Guardar como código de barras» (grava-o
 *    no Odoo, sem contar) ou «É a série — contar» (o «contar mesmo assim»);
 *  - **o aviso «ativar o número de série» cancelado**: «É a série (ativar o
 *    nº de série)» volta a abrir o aviso para esse código; «Guardar como
 *    código de barras» grava-o no Odoo depois do aviso «não é um EAN», sem contar;
 *  - **lido com o aviso aberto**: não conta; fica com as mesmas escolhas
 *    (pelo aspeto do código: EAN ou não) para resolver depois de responder.
 *
 * Num artigo com séries, um código que o Odoo não conhece conta logo como
 * série (0.45.1); a resposta traz «Não é série — Guardar como código de
 * barras» (`offerNotSerial`, `saveAsBarcode`): tira a série e grava o código.
 *
 * Puro (corre com `pnpm test`).
 */

export type SerialChoice = "guardar_codigo" | "serie_contar" | "serie_ativar"

/** O que levou às escolhas. */
export type ChoiceTrigger = "parece_ean" | "ativacao_cancelada" | "aviso_aberto"

export interface ChoiceTarget {
  tracking: string | null
  /** Artigo sem rastreio com «Ler séries». */
  asSerial?: boolean
  /** A referência do artigo (a etiqueta maisenergia): é o código do artigo, nunca série. */
  default_code?: string | null
}

export const CHOICE_LABEL: Record<SerialChoice, string> = {
  guardar_codigo: "Guardar como código de barras",
  serie_contar: "É a série — contar",
  serie_ativar: "É a série (ativar o nº de série)",
}

/** Numa série acabada de contar (0.45.1): tira-a e grava o código no artigo. */
export const NOT_SERIAL_LABEL = "Não é série — Guardar como código de barras"

/**
 * O botão «Não é série — …» de uma série contada: **não pára** o leitor
 * nativo da app de campo (`blocking: false`) — ler 10 séries de contadores não
 * fecha o leitor 10 vezes; o botão fica nas recentes da página. As outras
 * escolhas (EAN, ativar o nº de série, associar) param, como antes.
 */
export function notSerialAction<T>(run: () => Promise<T>): { label: string; blocking: false; run: () => Promise<T> } {
  return { label: NOT_SERIAL_LABEL, blocking: false, run }
}

/** O aviso antes de gravar um código que não é EAN. */
export const NON_GTIN_WARNING = "Este código não é um EAN. Se for o número de série desta caixa (muda de caixa para caixa), não o guarde como código de barras."

/** A ler séries: artigo com séries, ou sem rastreio com «Ler séries» ligado. */
export function inSerialMode(target: ChoiceTarget | null | undefined): boolean {
  if (!target) return false
  return target.tracking === "serial" || (target.tracking === "none" && target.asSerial === true)
}

/** As escolhas para um código lido a ler séries (vazio fora do modo séries). */
export function serialModeChoices(trigger: ChoiceTrigger, code: string, target: ChoiceTarget | null | undefined): SerialChoice[] {
  if (!inSerialMode(target)) return []
  const ean = isValidGtin(normalizeCode(code))
  if (trigger === "parece_ean" || ean) return ["guardar_codigo", "serie_contar"]
  // Num artigo com séries um código que não é EAN conta logo; só sem rastreio é preciso ativar.
  return target!.tracking === "serial" ? ["serie_contar", "guardar_codigo"] : ["serie_ativar", "guardar_codigo"]
}

/** A referência do próprio artigo (sem perguntar ao servidor: com o aviso aberto não se lê nada). */
export function isOwnCode(code: string, target: ChoiceTarget | null | undefined): boolean {
  return !!target?.default_code && normalizeCode(target.default_code) === normalizeCode(code)
}

/** Gravar o código no artigo foi recusado (já tem outro, é de outro artigo…): ficam só as escolhas de série. */
export function withoutAssociation(choices: SerialChoice[]): SerialChoice[] {
  return choices.filter((c) => c !== "guardar_codigo")
}

/** «Guardar como código de barras»: um código que não é EAN só depois do aviso (`allow_non_gtin`). */
export function needsNonGtinWarning(code: string): boolean {
  return !isValidGtin(normalizeCode(code))
}

/** O que interessa da resposta de uma leitura para «Não é série». */
export interface ScanForNotSerial {
  result: string
  maybe_product_code?: boolean
  line?: { id: string } | null
  product?: { tracking: string } | null
}

/**
 * «Não é série — Guardar como código de barras» numa série contada (ou já
 * lida) de um artigo com séries, quando o servidor diz que o Odoo não conhece
 * o código (`maybe_product_code`) e há a linha para tirar. Nunca quando quem
 * lê acabou de dizer que é a série («É a série — contar»: `forced`).
 */
export function offerNotSerial(r: ScanForNotSerial, opts: { forced?: boolean } = {}): boolean {
  return (
    !opts.forced &&
    (r.result === "serie" || r.result === "ja_lida") &&
    r.maybe_product_code === true &&
    !!r.line?.id &&
    r.product?.tracking === "serial"
  )
}

/** Os pedidos que gravar um código precisa (o ecrã liga-os à API; os testes, a falsos). */
export interface SaveBarcodeDeps {
  /** O aviso «não é um EAN» (diálogo da app): true = gravar mesmo assim. */
  confirmNonGtin: (code: string) => Promise<boolean>
  /** PATCH `remove_line`. */
  removeLine: (lineId: string) => Promise<unknown>
  /** POST `associate` com `count: false`. */
  associate: (body: { product_id: number; code: string; count: false; allow_non_gtin?: true }) => Promise<{ code: string; message: string }>
}

export type SaveBarcodeOutcome =
  /** Gravado (ou já era o código); `removed`: a série saiu da contagem. */
  | { kind: "saved"; code: string; message: string; removed: boolean }
  /** O aviso «não é um EAN» foi cancelado: nada mudou (a série continua contada). */
  | { kind: "declined" }
  /** Recusado pelo servidor (artigo já com outro código, código de outro artigo, série/lote, linha já aplicada…). */
  | { kind: "refused"; message: string; removed: boolean }
  /** Sem rede: `removed` diz se a série já tinha saído. */
  | { kind: "offline"; removed: boolean }

const offline = (e: unknown) => (e as { status?: number | null } | null)?.status === null

/**
 * «Guardar como código de barras» (com `lineId`: «Não é série — …»): o aviso
 * «não é um EAN» primeiro (cancelar não mexe em nada), depois tira a série
 * contada (`remove_line`) e grava o código no artigo sem contar
 * (`count: false`, `allow_non_gtin` só depois do aviso). As guardas são as do
 * servidor; a recusa volta com a mensagem dele.
 */
export async function saveAsBarcode(deps: SaveBarcodeDeps, input: { product_id: number; code: string; lineId?: string | null }): Promise<SaveBarcodeOutcome> {
  const nonGtin = needsNonGtinWarning(input.code)
  if (nonGtin && !(await deps.confirmNonGtin(input.code))) return { kind: "declined" }
  let removed = false
  if (input.lineId) {
    try {
      await deps.removeLine(input.lineId)
      removed = true
    } catch (e) {
      return offline(e) ? { kind: "offline", removed } : { kind: "refused", message: (e as Error).message, removed }
    }
  }
  try {
    const r = await deps.associate({ product_id: input.product_id, code: input.code, count: false, ...(nonGtin ? { allow_non_gtin: true as const } : {}) })
    return { kind: "saved", code: r.code, message: r.message, removed }
  } catch (e) {
    return offline(e) ? { kind: "offline", removed } : { kind: "refused", message: (e as Error).message, removed }
  }
}
